package core.validation.field

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import core.validation.EsInputState
import core.validation.validator.DoubleValidator

/**
 * Represents a field that can be validated.
 *
 * @param firstInitialValue The initial value of the first field.
 * @param secondInitialValue The initial value of the second field.
 * @param validators The list of validators to apply to the field.
 */
open class DoubleFieldImpl<T : Any>(
    firstInitialValue: T,
    secondInitialValue: T,
    private vararg val validators: DoubleValidator<T>,
) : Field {
    /**
     * The error message of the first field.
     */
    var firstErrorMessage by mutableStateOf<String?>(null)

    /**
     * The error message of the second field.
     */
    var secondErrorMessage by mutableStateOf<String?>(null)

    /**
     * The state of the first field.
     */
    var firstState by mutableStateOf(EsInputState.Default)

    /**
     * The state of the second field.
     */
    var secondState by mutableStateOf(EsInputState.Default)

    override val state: EsInputState
        get() =
            if (firstState.isError || secondState.isError) {
                EsInputState.Error
            } else {
                EsInputState.Default
            }

    /**
     * The value of the first field.
     */
    var firstValue: T = firstInitialValue

    /**
     * The value of the second field.
     */
    var secondValue: T = secondInitialValue

    /**
     * Validates the field using the current value.
     */
    override fun validate(): Boolean =
        validate(
            firstValue = firstValue,
            secondValue = secondValue,
        )

    /**
     * Validates the field.
     *
     * The field is considered valid if all the validators pass.
     *
     * The state of the field is updated to [EsInputState.Success] if the value is valid, and [EsInputState.Error] otherwise.
     *
     * The error message is set to the error message of the first validator that fails, or `null` if the value is valid.
     *
     * @param firstValue The first value to validate.
     * @param secondValue The second value to validate.
     * @return `true` if the value is valid, `false` otherwise.
     */
    fun validate(
        firstValue: T,
        secondValue: T,
    ): Boolean {
        this.firstValue = firstValue
        this.secondValue = secondValue

        var isFirstValueValid = true
        var isSecondValueValid = true

        val failedValidator =
            validators.firstOrNull {
                val (first, second) = it.validate(firstValue, secondValue)

                isFirstValueValid = first
                isSecondValueValid = second

                !first || !second
            }

        firstState =
            if (isFirstValueValid) {
                EsInputState.Default
            } else {
                EsInputState.Error
            }

        secondState =
            if (isSecondValueValid) {
                EsInputState.Default
            } else {
                EsInputState.Error
            }

        firstErrorMessage =
            if (isFirstValueValid) {
                null
            } else {
                failedValidator?.errorMessage
            }

        secondErrorMessage =
            if (isSecondValueValid) {
                null
            } else {
                failedValidator?.errorMessage
            }

        return failedValidator == null
    }

    /**
     * Resets the field.
     */
    override fun reset() {
        firstState = EsInputState.Default
        secondState = EsInputState.Default
        firstErrorMessage = null
        secondErrorMessage = null
    }
}
